<template>
  <AppLayout>
    <div class="container">
      <h6 class="fw-bold text-primary">Bulk Upload Suppliers</h6>
      <p>Upload a CSV manifest plus supplier documents.</p>

      <div class="panel">
        <div class="row">
          <div class="col-md-8">
            <label class="form-label">Select files (CSV + documents)</label>
            <input type="file" multiple @change="onFilesChange" ref="filesInput" :disabled="uploading" class="form-control" />
            <small class="text-muted">You can select one manifest CSV and any number of files. Filenames in CSV should match uploaded files.</small>
          </div>
          <div class="col-md-4 d-flex align-items-end gap-2">
            <button class="btn btn-primary w-50" :disabled="uploading || files.length === 0" @click="submit">Upload</button>
            <button class="btn btn-style-2 w-50" @click="downloadSample" :disabled="uploading">Download sample CSV</button>
          </div>
        </div>

        <div class="mt-3">
          <div v-if="uploading || uploadPercent > 0">
            <div class="d-flex justify-content-between mb-1">
              <small>Uploading</small>
              <small>{{ progressText }}</small>
            </div>
            <div class="progress" style="height: 18px;">
              <div class="progress-bar" role="progressbar" :style="{ width: uploadPercent + '%' }" :aria-valuenow="uploadPercent" aria-valuemin="0" aria-valuemax="100">{{ uploadPercent }}%</div>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <div v-if="result && result.status === true" class="alert alert-success">
            Upload completed: {{ result?.results?.created || 0 }} suppliers created.
          </div>
          <div v-else-if="result && result.status === false" class="alert alert-danger">
            Upload failed: {{ result.message || 'See details below' }}
          </div>

          <!-- always show server response for debugging when present -->
          <div v-else-if="result" class="alert alert-info">
            <strong>Server response</strong>
            <pre style="white-space:pre-wrap">{{ resultText }}</pre>
          </div>

          <div v-if="result && result.data && result.data.length">
            <h6>Details</h6>
            <pre style="white-space:pre-wrap">{{ resultText }}</pre>
          </div>

          <div v-if="errors && errors.length" class="error-list">
            <h6 class="text-danger">Row errors</h6>
            <div class="errors-grid">
              <div v-for="(e, i) in errors" :key="i" class="error-card">
                <div class="error-card-header">
                  <strong>Row {{ e.line || i + 2 }}</strong>
                </div>
                <div class="error-card-body">
                  <template v-if="e.errors && e.errors.length">
                    <ul class="error-issues">
                      <li v-for="(errMsg, idx) in e.errors" :key="idx" class="error-issue">
                        <span class="error-badge">Error</span>
                        <span class="error-text">{{ errMsg }}</span>
                      </li>
                    </ul>
                  </template>
                  <template v-else-if="e.error">
                    <div class="error-single">
                      <span class="error-badge">Error</span>
                      <span class="error-text">{{ e.error }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="error-single">
                      <span class="error-text">{{ JSON.stringify(e) }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue'
import axios from 'axios'

export default {
  name: 'BulkUploadSuppliers',
  components: { AppLayout },
  data() {
    return {
      files: [],
      uploading: false,
      progressText: '',
      uploadPercent: 0,
      result: null,
      errors: [],
    }
  },
  computed: {
    resultText() {
      try { return JSON.stringify(this.result, null, 2) } catch (e) { return String(this.result) }
    }
  },
  methods: {
    onFilesChange(e) {
      const inputFiles = Array.from(e.target.files || [])
      this.files = inputFiles
    },
    downloadSample() {
      const headers = ['name','idNumber','business','address','contact','phone','email','prequalified','procurementCategory','documentFileName','documentExpiry','supplierDocumentType']
      const sample = [headers.join(','), 'Acme Corp,ID123,Construction,123 Street,John Doe,+254700000000,john@acme.com,true,Construction,acme_doc.pdf,2026-12-31,ID Document']
      const blob = new Blob([sample.join('\n')], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'suppliers-sample.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    async submit() {
      if (!this.files.length) return
      this.uploading = true
      this.result = null
      this.errors = []
      const form = new FormData()
      this.files.forEach(f => form.append('files', f, f.name))

      try {
        const token = JSON.parse(localStorage.getItem('access_token'))
        const res = await axios.post((process.env.VUE_APP_URL || '') + '/suppliers/bulk', form, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              this.uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              this.progressText = this.uploadPercent + '%'
            }
          }
        })
        this.result = res.data
        if (res.data?.results?.errors) this.errors = res.data.results.errors
      } catch (err) {
        this.result = err.response?.data || { message: err.message }
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.panel{ padding: 20px; margin-top: 10px }
.form-control { padding: .5rem .75rem; }
.btn { min-height: 38px; }

.error-list { margin-top: 16px; }
.errors-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 11px; }
.error-card { border: 1px solid #f5c6cb; background: #fff6f6; padding: 11px; border-radius: 6px; }
.error-card-header { margin-bottom: 8px; font-weight: 700; color: #a71d2a; }
.error-issues { list-style: none; padding: 0; margin: 0; }
.error-issue { display:flex; align-items:flex-start; gap:8px; margin-bottom:6px; }
.error-badge { background: #a71d2a; color: #fff; padding: 2px 8px; border-radius: 11px; font-size: 11px; font-weight:700; }
.error-text { color:#333; font-size:11px; word-break:break-word; }
.error-single { display:flex; gap:8px; align-items:center; }

@media (max-width: 576px) {
  .errors-grid { grid-template-columns: 1fr; }
}
</style>
